import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { KeyRound, ArrowLeft, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Reset link sent if email exists");
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {LOGO_URL && <img src={LOGO_URL} alt="OSAA" className="h-16 mx-auto mb-4" />}
          <h1 className="font-heading text-2xl font-medium text-foreground">{token ? "Set New Password" : "Forgot Password"}</h1>
        </div>
        <div className="bg-card border border-border/60 p-8 rounded-sm shadow-sm">
          {!token ? (
            sent ? (
              <div className="text-center">
                <Mail className="w-12 h-12 text-accent mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">Check your email</p>
                <p className="text-sm text-muted-foreground mb-6">If an account exists, we've sent a password reset link.</p>
                <Link to="/login" className="text-sm text-primary hover:text-accent">Back to Login</Link>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-5">
                <p className="text-sm text-muted-foreground mb-4">Enter your email and we'll send a reset link.</p>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Email</label>
                  <Input type="email" placeholder="you@school.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-primary text-primary-foreground text-sm font-medium rounded-sm hover:bg-primary/90 disabled:opacity-50">
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            )
          ) : (
            done ? (
              <div className="text-center">
                <KeyRound className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">Password Reset!</p>
                <Link to="/login" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-sm rounded-sm mt-4">Login Now</Link>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">New Password</label>
                  <Input type="password" placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Confirm Password</label>
                  <Input type="password" placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)} />
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 bg-primary text-primary-foreground text-sm font-medium rounded-sm hover:bg-primary/90 disabled:opacity-50">
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            )
          )}
        </div>
        <div className="text-center mt-4">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
